import React from "react";

import { getPrincipal } from "@p2l/config";
import { useTranslations } from "@p2l/react-utils";
import {
  Dialog,
  DialogBody,
  Button,
  DialogTitle,
  TextArea,
  IconTooltip,
  TextField,
  Switch
} from "@p2l/ui-public";
import { faCheckCircle } from "@p2l/icons/fa-solid";

import type { FeedbackDialogProps } from "./feedback-dialog.props";
import {
  DialogContent,
  DialogTitleWrapper,
  StyledDialogFooter,
  StyledFAIcon,
  StyledFormControl,
  StyledSuccessMessage,
  SuccessBodyWrapper
} from "./feedback-dialog.styles";

import translations from "./translations.json";

export const FeedbackDialog = (props: FeedbackDialogProps) => {
  const { translate } = useTranslations(translations);
  let loginType = getPrincipal().id ? "user" : "company";

  const [form, setForm] = React.useState<{
    formText: string;
    canBeContacted: boolean;
    formName?: string;
    formEmail?: string;
    formRepeatEmail?: string;
  }>({
    formText: "",
    canBeContacted: false,
  });

  const [validationMessages, setValidationMessages] = React.useState({
    formText: "",
    formName: "",
    formEmail: "",
    formRepeatEmail: "",
  });

  const handleClose = () => {
    props.onClose();
    setForm({
      formText: "",
      canBeContacted: false,
    });
  };

  React.useEffect(() => {
    if (loginType === "company") {
      setForm((prevForm) => ({
        ...prevForm,
        formName: "",
        formEmail: "",
        formRepeatEmail: "",
      }));
    }
  }, [loginType]);

  const onFormChange = (key: string, value: any) => {
    setForm({
      ...form,
      [key]: value,
    });
  };

  const validateForm = () => {
    const newValidationMessages = {
      formText: "",
      formName: "",
      formEmail: "",
      formRepeatEmail: "",
    };

    if (!form.formText) {
      newValidationMessages.formText = translate("COMMON_REQUIRED");
    }

    if (loginType === "company") {
      if (!form.formName) {
        newValidationMessages.formName = translate("COMMON_REQUIRED");
      }
      if (!form.formEmail) {
        newValidationMessages.formEmail = translate("COMMON_REQUIRED");
      } else if (
        !form.formEmail.match(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        )
      ) {
        newValidationMessages.formEmail = translate("INVALID_EMAIL");
      }

      if (!form.formRepeatEmail) {
        newValidationMessages.formRepeatEmail = translate("COMMON_REQUIRED");
      } else if (form.formRepeatEmail !== form.formEmail) {
        newValidationMessages.formRepeatEmail = translate("EMAIL_MUST_MATCH");
      }
    }

    setValidationMessages(newValidationMessages);

    return Object.values(newValidationMessages).every((m) => m === "");
  };

  const handleSend = () => {
    if (!validateForm()) {
      return;
    }

    props.onSend(
      form.formText,
      form.canBeContacted,
      form.formName,
      form.formEmail
    );
  };

  return (
    <Dialog
      interceptPopState
      open={props.open}
      onClose={handleClose}
    >
      <DialogTitleWrapper>
        <DialogTitle>{props.title}</DialogTitle>
      </DialogTitleWrapper>
      <DialogBody>
        <DialogContent>
          {props.successfulFeedbackSave ? (
            <SuccessBodyWrapper>
              <StyledFAIcon icon={faCheckCircle} size={"4x"} />
              <StyledSuccessMessage>
                {translate("FEEDBACK_SUCCESSFUL_SEND")}
              </StyledSuccessMessage>
            </SuccessBodyWrapper>
          ) : (
            <>
              {loginType === "company" && (
                <>
                  <StyledFormControl>
                    <TextField
                      onChange={(e) => onFormChange("formName", e.target.value)}
                      value={form.formName!}
                      error={!!validationMessages.formName && !form.formName}
                      errorText={validationMessages.formName}
                      placeholder={translate("NAME")}
                    />
                  </StyledFormControl>
                  <StyledFormControl>
                    <TextField
                      onChange={(e) =>
                        onFormChange("formEmail", e.target.value)
                      }
                      value={form.formEmail!}
                      error={!!validationMessages.formEmail && !form.formEmail}
                      errorText={validationMessages.formEmail}
                      placeholder={translate("EMAIL")}
                    />
                  </StyledFormControl>
                  <StyledFormControl>
                    <TextField
                      onChange={(e) =>
                        onFormChange("formRepeatEmail", e.target.value)
                      }
                      value={form.formRepeatEmail!}
                      error={
                        !!validationMessages.formRepeatEmail &&
                        !form.formRepeatEmail
                      }
                      errorText={validationMessages.formRepeatEmail}
                      placeholder={translate("REPEAT_EMAIL")}
                    />
                  </StyledFormControl>
                </>
              )}

              <StyledFormControl>
                <TextArea
                  textareaProps={{
                    rows: 4,
                    placeholder: translate("FEEDBACK_TEXT_AREA_PLACEHOLDER"),
                  }}
                  value={form.formText}
                  onChange={(e) => onFormChange("formText", e.target.value)}
                  error={!!validationMessages.formText && !form.formText}
                  errorText={validationMessages.formText}
                />
              </StyledFormControl>
              <StyledFormControl>
                <Switch
                  checked={form.canBeContacted}
                  onChange={(checked) =>
                    onFormChange("canBeContacted", checked)
                  }
                  label={translate("FEEDBACK_CAN_BE_CONTACTED")}
                />{" "}
                <IconTooltip
                  content={translate("FEEDBACK_CAN_BE_CONTACTED_TOOLTIP")}
                />
              </StyledFormControl>
            </>
          )}
        </DialogContent>
      </DialogBody>
      <StyledDialogFooter>
        {props.successfulFeedbackSave ? (
          <Button onClick={handleClose}>{translate("CLOSE")}</Button>
        ) : (
          <>
            <Button onClick={handleClose}>{translate("CANCEL")}</Button>
            <Button variant="filled" color="info" onClick={handleSend}>
              {translate("SEND_FEEDBACK")}
            </Button>
          </>
        )}
      </StyledDialogFooter>
    </Dialog>
  );
};
